import React, { useState, useRef } from 'react';
import { useConnection, useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { Keypair, PublicKey, Transaction, SystemProgram, TransactionInstruction } from '@solana/web3.js';
import {
  createInitializeMint2Instruction,
  createAssociatedTokenAccountInstruction,
  createMintToInstruction,
  createSetAuthorityInstruction,
  AuthorityType,
  getAssociatedTokenAddress,
  TOKEN_PROGRAM_ID,
  MINT_SIZE,
  getMinimumBalanceForRentExemptMint,
} from '@solana/spl-token';
import styled from 'styled-components';
import { supabase, TABLES, STORAGE_BUCKETS, uploadImageToStorage, uploadBlobToStorage } from '../../utils/supabase';
import { createOrcaPool } from '../../utils/orcaPoolCreator';
import { MAVRK_PLATFORM_FEE_WALLET } from '../../utils/solanaConstants';
import { swapSolForToken } from '../../utils/orcaSwap';

// Solana Brand Colors
const SOL_PURPLE = '#9945FF';
const SOL_GREEN = '#14F195';

const TOKEN_METADATA_PROGRAM_ID = new PublicKey('metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s');
const WSOL_MINT = new PublicKey("So11111111111111111111111111111111111111112");

function createMetadataInstruction(
  metadataPDA: PublicKey,
  mint: PublicKey,
  mintAuthority: PublicKey,
  payer: PublicKey,
  updateAuthority: PublicKey,
  name: string,
  symbol: string,
  uri: string
): TransactionInstruction {
  const nameBytes = Buffer.from(name);
  const symbolBytes = Buffer.from(symbol);
  const uriBytes = Buffer.from(uri);
  const dataSize = 1 + 4 + nameBytes.length + 4 + symbolBytes.length + 4 + uriBytes.length + 2 + 1 + 1 + 1 + 1 + 1;
  const data = Buffer.alloc(dataSize);
  let offset = 0;
  data.writeUInt8(33, offset); offset += 1;
  data.writeUInt32LE(nameBytes.length, offset); offset += 4;
  nameBytes.copy(data, offset); offset += nameBytes.length;
  data.writeUInt32LE(symbolBytes.length, offset); offset += 4;
  symbolBytes.copy(data, offset); offset += symbolBytes.length;
  data.writeUInt32LE(uriBytes.length, offset); offset += 4;
  uriBytes.copy(data, offset); offset += uriBytes.length;
  data.writeUInt16LE(0, offset); offset += 2;
  data.writeUInt8(0, offset); offset += 1;
  data.writeUInt8(0, offset); offset += 1;
  data.writeUInt8(0, offset); offset += 1;
  data.writeUInt8(1, offset); offset += 1;
  data.writeUInt8(0, offset);
  return new TransactionInstruction({
    keys: [
      { pubkey: metadataPDA, isSigner: false, isWritable: true },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: mintAuthority, isSigner: true, isWritable: false },
      { pubkey: payer, isSigner: true, isWritable: true },
      { pubkey: updateAuthority, isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ],
    programId: TOKEN_METADATA_PROGRAM_ID,
    data: data,
  });
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #FFFFFF;
  font-family: 'Inter', sans-serif;
  margin: 0 0 24px 0;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const WalletButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
  
  .wallet-adapter-button {
    background: linear-gradient(135deg, ${SOL_PURPLE} 0%, ${SOL_GREEN} 100%) !important;
    border-radius: 12px !important;
    font-weight: 600 !important;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #FFFFFF;
  font-family: 'Inter', sans-serif;
  margin-bottom: 8px;
  
  .required {
    color: #ff4444;
  }
`;

const Input = styled.input`
  width: 100%;
  background: #1A1A1A;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  color: #FFFFFF;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    background: #252525;
    border-color: ${SOL_PURPLE};
    box-shadow: 0 0 0 3px rgba(153, 69, 255, 0.1);
  }

  &::placeholder {
    color: #666666;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  background: #1A1A1A;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  color: #FFFFFF;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  transition: all 0.2s ease;
  resize: vertical;

  &:focus {
    background: #252525;
    border-color: ${SOL_PURPLE};
    box-shadow: 0 0 0 3px rgba(153, 69, 255, 0.1);
  }

  &::placeholder {
    color: #666666;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: block;
  width: 100%;
  background: #1A1A1A;
  border: 2px dashed rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #999999;
  font-family: 'Inter', sans-serif;
  font-size: 14px;

  &:hover {
    background: #252525;
    border-color: ${SOL_PURPLE};
    color: ${SOL_PURPLE};
  }
`;

const LogoPreview = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin: 12px auto;
  display: block;
`;

const Button = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 14px 24px;
  background: ${props => props.disabled ? '#333333' : `linear-gradient(135deg, ${SOL_PURPLE} 0%, ${SOL_GREEN} 100%)`};
  border: none;
  border-radius: 8px;
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  margin-top: 8px;
  box-shadow: ${props => props.disabled ? 'none' : '0 4px 12px rgba(153, 69, 255, 0.3)'};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(153, 69, 255, 0.4);
  }
`;

const LogArea = styled.pre`
  background: #0a0a0a;
  padding: 16px;
  border-radius: 8px;
  font-family: monospace;
  font-size: 12px;
  color: ${SOL_GREEN};
  overflow-x: auto;
  white-space: pre-wrap;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid rgba(20, 241, 149, 0.2);
`;

interface SolanaCreatorFormContentProps {
  onSuccess?: () => void;
  onClose?: () => void;
}

export const SolanaCreatorFormContent: React.FC<SolanaCreatorFormContentProps> = ({ onSuccess, onClose }) => {
  const { connection } = useConnection();
  const wallet = useWallet();
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [logs, setLogs] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [initialBuyAmount, setInitialBuyAmount] = useState<number>(0.0001);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const TOKENS_PER_SOL = 41_000_000;
  const INITIAL_PRICE_SOL = 1 / TOKENS_PER_SOL;

  const addLog = (msg: string) => setLogs(prev => [...prev, msg]);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const generateVanityMint = async (): Promise<Keypair> => {
    addLog("🔍 Generating Vanity Mint (starts with 'm')...");
    let attempts = 0;
    const startTime = Date.now();

    while (true) {
      const keypair = Keypair.generate();
      attempts++;

      if (keypair.publicKey.toString().startsWith("m")) {
        const mintBytes = keypair.publicKey.toBuffer();
        const wsolBytes = WSOL_MINT.toBuffer();
        let compare = 0;
        for (let i = 0; i < 32; i++) {
          if (mintBytes[i] > wsolBytes[i]) { compare = 1; break; }
          else if (mintBytes[i] < wsolBytes[i]) { compare = -1; break; }
        }
        if (compare > 0) {
          const elapsed = ((Date.now() - startTime) / 1000).toFixed(2);
          addLog(`✅ Found after ${attempts} attempts in ${elapsed}s!`);
          addLog(`   Mint: ${keypair.publicKey.toString()}`);
          return keypair;
        }
      }
      if (attempts % 100 === 0) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }
  };

  const handleCreate = async () => {
    if (!wallet.publicKey || !wallet.signTransaction) {
      addLog("❌ Wallet not connected!");
      return;
    }
    if (!name || !symbol) {
      addLog("❌ Please enter Name and Symbol");
      return;
    }
    if (initialBuyAmount < 0.0001 || initialBuyAmount > 1) {
      addLog("❌ Initial buy amount must be between 0.0001 and 1 SOL");
      return;
    }

    setIsCreating(true);
    setLogs([]);

    try {
      const mintKeypair = await generateVanityMint();
      const mintAddress = mintKeypair.publicKey.toString();

      let logoUrl = '';
      if (imageFile) {
        addLog("📤 Uploading logo to Supabase...");
        const fileName = `${mintAddress}-logo.png`;
        const uploadedUrl = await uploadImageToStorage(imageFile, STORAGE_BUCKETS.SOLANA_LOGOS, fileName);
        if (uploadedUrl) {
          logoUrl = uploadedUrl;
          addLog("✅ Logo uploaded successfully!");
        }
      }

      addLog("📄 Generating and uploading metadata JSON...");
      const metadata = {
        name, symbol, description, image: logoUrl,
        properties: { files: [{ uri: logoUrl, type: "image/png" }], category: "image" }
      };
      const metadataBlob = new Blob([JSON.stringify(metadata)], { type: 'application/json' });
      const metadataFileName = `${mintAddress}.json`;
      const metadataUrl = await uploadBlobToStorage(metadataBlob, STORAGE_BUCKETS.SOLANA_LOGOS, metadataFileName);
      if (!metadataUrl) {
        throw new Error("Failed to upload metadata JSON. Cannot proceed.");
      }
      addLog("✅ Metadata JSON uploaded successfully!");

      addLog("⏳ Building transaction...");
      const tx = new Transaction();
      const lamports = await getMinimumBalanceForRentExemptMint(connection);

      tx.add(SystemProgram.createAccount({
        fromPubkey: wallet.publicKey,
        newAccountPubkey: mintKeypair.publicKey,
        space: MINT_SIZE,
        lamports,
        programId: TOKEN_PROGRAM_ID,
      }));

      tx.add(createInitializeMint2Instruction(mintKeypair.publicKey, 9, wallet.publicKey, wallet.publicKey, TOKEN_PROGRAM_ID));

      const [metadataPDA] = PublicKey.findProgramAddressSync(
        [Buffer.from('metadata'), TOKEN_METADATA_PROGRAM_ID.toBuffer(), mintKeypair.publicKey.toBuffer()],
        TOKEN_METADATA_PROGRAM_ID
      );
      tx.add(createMetadataInstruction(metadataPDA, mintKeypair.publicKey, wallet.publicKey, wallet.publicKey, wallet.publicKey, name, symbol, metadataUrl));

      const ata = await getAssociatedTokenAddress(mintKeypair.publicKey, wallet.publicKey);
      tx.add(createAssociatedTokenAccountInstruction(wallet.publicKey, ata, wallet.publicKey, mintKeypair.publicKey));

      const supply = BigInt("1000000000000000000");
      tx.add(createMintToInstruction(mintKeypair.publicKey, ata, wallet.publicKey, supply));
      tx.add(createSetAuthorityInstruction(mintKeypair.publicKey, wallet.publicKey, AuthorityType.MintTokens, null));
      tx.add(createSetAuthorityInstruction(mintKeypair.publicKey, wallet.publicKey, AuthorityType.FreezeAccount, null));

      tx.feePayer = wallet.publicKey;
      addLog("⏳ Fetching blockhash...");
      const latestBlockhash = await connection.getLatestBlockhash('confirmed');
      tx.recentBlockhash = latestBlockhash.blockhash;

      // Phantom wallet signs first (required for Lighthouse security)
      addLog("⏳ Signing transaction with wallet...");
      let signedTx = await wallet.signTransaction!(tx);
      
      // Additional signers sign afterward (mint keypair required for createAccount)
      signedTx.partialSign(mintKeypair);

      addLog("⏳ Sending transaction...");
      const sig = await connection.sendRawTransaction(signedTx.serialize(), {
        skipPreflight: false,
        maxRetries: 3,
      });
      addLog(`✅ Transaction Sent! Signature: ${sig}`);

      await connection.confirmTransaction({
        signature: sig,
        blockhash: latestBlockhash.blockhash,
        lastValidBlockHeight: latestBlockhash.lastValidBlockHeight
      }, 'confirmed');

      addLog("🎉 Token Creation Complete!");
      addLog(`   Mint Address: ${mintAddress}`);

      let whirlpoolAddress = '';
      let tokenVaultA = '';
      let tokenVaultB = '';
      let tickSpacing = 128;
      addLog("🌊 Creating Orca Whirlpool LP...");
      
      try {
        const totalSupply = BigInt("999999999000000000");
        addLog(`   LP Amount: ${(Number(totalSupply) / 1e9).toLocaleString()} tokens (100%)`);
        addLog(`   Initial Price: ~41M tokens per SOL (~$3K market cap)`);
        
        const poolResult = await createOrcaPool({
          connection,
          wallet: {
            publicKey: wallet.publicKey!,
            signTransaction: wallet.signTransaction!,
            sendTransaction: wallet.sendTransaction,
          },
          tokenMint: mintKeypair.publicKey,
          tokenAmount: totalSupply,
          initialPriceSol: INITIAL_PRICE_SOL,
          onLog: addLog,
          protocolFeeWallet: MAVRK_PLATFORM_FEE_WALLET,  // Mandatory: Transfer LP to protocol for security
        });
        
        whirlpoolAddress = poolResult.whirlpoolAddress.toString();
        tokenVaultA = poolResult.tokenVaultA.toString();
        tokenVaultB = poolResult.tokenVaultB.toString();
        tickSpacing = poolResult.tickSpacing;
        
        addLog(`✅ Orca Whirlpool Created!`);
        addLog(`   Pool: ${whirlpoolAddress}`);
        
        addLog(`🚀 Executing initial buy (${initialBuyAmount} SOL) to activate pool...`);
        try {
          const initialBuyLamports = BigInt(Math.floor(initialBuyAmount * 1e9));
          const swapResult = await swapSolForToken({
            connection,
            wallet: {
              publicKey: wallet.publicKey!,
              signTransaction: wallet.signTransaction!,
              sendTransaction: wallet.sendTransaction,
            },
            whirlpoolAddress: poolResult.whirlpoolAddress,
            amountIn: initialBuyLamports,
            minAmountOut: BigInt(0),
            onLog: addLog,
          });
          
          addLog(`✅ Initial buy complete! Pool is now ACTIVE!`);
          addLog(`   You received ~${(Number(swapResult.estimatedAmountOut) / 1e9).toLocaleString()} tokens`);
        } catch (swapError: any) {
          console.error("Initial buy error:", swapError);
          addLog(`⚠️ Initial buy failed: ${swapError.message || swapError}`);
        }
      } catch (lpError: any) {
        console.error("LP Creation Error:", lpError);
        addLog(`⚠️ LP Creation failed: ${lpError.message}`);
      }

      addLog("💾 Saving token details to database...");
      const { error: dbError } = await supabase
        .from(TABLES.TOKENS_SOL)
        .insert({
          name, symbol, description: description || null,
          logo_url: logoUrl || null,
          mint_address: mintAddress,
          contract_address: mintAddress,
          deployer_address: wallet.publicKey.toString(),
          network: 'sol',
          decimals: 9,
          initial_buy_amount: '0',
          favorites_count: 0,
          pool_address: whirlpoolAddress || null,
          token_vault_a: tokenVaultA || null,
          token_vault_b: tokenVaultB || null,
          tick_spacing: tickSpacing || 128,
        });

      if (dbError) {
        console.error("Supabase DB Error:", dbError);
        addLog(`⚠️ Failed to save to DB: ${dbError.message}`);
      } else {
        addLog("✅ Token saved to database!");
      }

      if (onSuccess) {
        setTimeout(() => {
          onSuccess();
          if (onClose) onClose();
        }, 2000);
      }

    } catch (e: any) {
      console.error(e);
      addLog(`❌ Error: ${e.message || e}`);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <FormContainer>
      <Title>
        <img src="/landing-assets/Solana.png" alt="Solana" style={{ width: 28, height: 28, borderRadius: '50%' }} />
        Create Solana Token
      </Title>
      
      <WalletButtonWrapper>
        <WalletMultiButton />
      </WalletButtonWrapper>

      <FormGroup>
        <Label>
          Token Name <span className="required">*</span>
        </Label>
        <Input
          type="text"
          placeholder="My Token"
          value={name}
          onChange={e => setName(e.target.value)}
          disabled={isCreating}
        />
      </FormGroup>

      <FormGroup>
        <Label>
          Token Symbol <span className="required">*</span>
        </Label>
        <Input
          type="text"
          placeholder="TKN"
          value={symbol}
          onChange={e => setSymbol(e.target.value.toUpperCase())}
          disabled={isCreating}
          maxLength={10}
        />
      </FormGroup>

      <FormGroup>
        <Label>Description (Optional)</Label>
        <TextArea
          placeholder="Describe your token..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={isCreating}
          style={{ minHeight: '80px' }}
        />
      </FormGroup>

      <FormGroup>
        <Label>Token Logo (Optional)</Label>
        <FileInput
          ref={logoInputRef}
          type="file"
          accept="image/*"
          onChange={handleLogoUpload}
          disabled={isCreating}
        />
        <FileInputLabel
          onClick={(e) => {
            if (!isCreating) {
              logoInputRef.current?.click();
            }
            e.preventDefault();
          }}
        >
          {logoPreview ? (
            <>
              <LogoPreview src={logoPreview} alt="Logo preview" />
              <div>Click to change logo</div>
            </>
          ) : (
            'Click to upload logo (PNG, JPG, etc.)'
          )}
        </FileInputLabel>
      </FormGroup>

      <FormGroup>
        <Label>Initial Buy Amount (SOL)</Label>
        <Input
          type="number"
          step="0.0001"
          min="0.0001"
          max="1"
          value={initialBuyAmount}
          onChange={e => {
            const value = parseFloat(e.target.value);
            if (!isNaN(value) && value >= 0.0001 && value <= 1) {
              setInitialBuyAmount(value);
            } else if (e.target.value === '' || value < 0.0001) {
              setInitialBuyAmount(0.0001);
            } else if (value > 1) {
              setInitialBuyAmount(1);
            }
          }}
          disabled={isCreating}
          placeholder="0.0001"
        />
        <div style={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '4px' }}>
          Amount of SOL to buy at launch (0.0001 - 1 SOL). Default: 0.0001 SOL
        </div>
      </FormGroup>

      <Button onClick={handleCreate} disabled={isCreating || !wallet.connected}>
        {isCreating ? 'Creating...' : 'Create Token + Orca LP'}
      </Button>

      {logs.length > 0 && (
        <LogArea>
          {logs.map((log, i) => <div key={i}>{log}</div>)}
        </LogArea>
      )}
    </FormContainer>
  );
};

